import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { ROZOSTRENI_NAVIGACE } from '../../lib/uiTokens';

/**
 * MobilniPanel — třetí sloupec modulu na mobilu (norma FofrLama OS).
 *
 * Na desktopu je panel stálý sloupec vedle detailu. Na telefonu se tři sloupce
 * vedle sebe nevejdou, ale to, co panel nese (statistika, kalkulace, revize),
 * se nemá kam schovat, aniž by zavazelo v detailu. Proto **jezdí zpod pravé
 * hrany**:
 *
 *   - **přejetím od pravé hrany doleva** — gesto se sbírá na celé ploše stupně,
 *     ne přes neviditelný pruh: panel se chytne, až když prst z pravých `ZONA`
 *     px opravdu jede vodorovně. Dokud jede svisle, patří tah obsahu pod ním
 *     (jinak by u pravého okraje nešel seznam scrollovat).
 *   - **klepnutím na úchyt** — kotva na pravé hraně. Bez ní by o gestu nikdo
 *     nevěděl; při prvním zobrazení dvakrát jemně kývne.
 *
 * Zavírá se stejným tahem zpátky (za hlavičku panelu), křížkem, klepnutím
 * vedle panelu nebo Esc.
 *
 * Obsah se staví **až při prvním otevření** a pak zůstává namontovaný — panel
 * si typicky dotahuje vlastní data a zavřít ho neznamená je zahodit.
 *
 * @param {{current: HTMLElement|null}} props.plochaRef - prvek, ze kterého se gesto sbírá
 * @param {boolean} props.otevreno
 * @param {() => void} props.onOtevri
 * @param {() => void} props.onZavri
 * @param {React.ReactNode|() => React.ReactNode} props.obsah
 * @param {string} [props.titulek]
 * @param {Function} [props.Ikona] - lucide ikona do úchytu a hlavičky
 * @param {string} [props.barva] - akcentová barva modulu
 * @param {boolean} [props.isDark]
 */
const SIRKA_MAX = 380;   // širší panel už na telefonu zakryje detail celý
const ZONA = 26;         // odkud u pravé hrany se dá začít táhnout
const PRAH = 0.32;       // část šířky, po které se puštění počítá jako otevření
const SMER = 10;         // px, po kterých se rozhodne, jestli je tah vodorovný

const sirkaPanelu = () => Math.min(SIRKA_MAX, Math.round((window.innerWidth || 360) * 0.86));

export default function MobilniPanel({
    plochaRef, otevreno, onOtevri, onZavri, obsah,
    titulek = 'Statistika', Ikona = null, barva = '#F59E0B', isDark = false,
    rezervaDole = null,
}) {
    const [sirka, setSirka] = useState(sirkaPanelu);
    const [taha, setTaha] = useState(false);
    const [posun, setPosun] = useState(0);     // 0 = zavřeno, 1 = otevřeno (drží se jen během tahu)
    const [bylOtevren, setBylOtevren] = useState(false);
    // Gesto čte polohu i mimo render (v posluchačích), takže ji vedle stavu drží ref.
    const posunRef = useRef(0);
    const zavriRef = useRef(onZavri);
    const otevriRef = useRef(onOtevri);
    zavriRef.current = onZavri;
    otevriRef.current = onOtevri;

    const nastavPosun = (v) => { posunRef.current = v; setPosun(v); };

    useEffect(() => {
        const h = () => setSirka(sirkaPanelu());
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    useEffect(() => {
        if (otevreno) setBylOtevren(true);
        nastavPosun(otevreno ? 1 : 0);
        setTaha(false);
    }, [otevreno]);

    useEffect(() => {
        if (!otevreno) return;
        const klavesa = (e) => { if (e.key === 'Escape') zavriRef.current?.(); };
        document.addEventListener('keydown', klavesa);
        return () => document.removeEventListener('keydown', klavesa);
    }, [otevreno]);

    // ── Otevírací gesto od pravé hrany ────────────────────────────────
    // Posluchače visí na ploše stupně, ne na vlastním pruhu — pruh by uzavřel
    // pravý okraj detailu a nešlo by na něm nic zmáčknout.
    useEffect(() => {
        const el = plochaRef?.current;
        if (!el || otevreno) return;
        let g = null;

        const start = (e) => {
            if (e.touches.length !== 1) return;
            const r = el.getBoundingClientRect();
            const t = e.touches[0];
            if (t.clientX < r.right - ZONA || t.clientY > r.bottom - 24) return;
            g = { x0: t.clientX, y0: t.clientY, vodo: false };
        };
        const move = (e) => {
            if (!g) return;
            const t = e.touches[0];
            const dx = g.x0 - t.clientX;
            const dy = Math.abs(t.clientY - g.y0);
            if (!g.vodo) {
                if (dy > SMER && dy > dx) { g = null; return; }   // svislý tah patří obsahu
                if (dx < SMER) return;
                g.vodo = true;
                setTaha(true);
            }
            e.preventDefault();
            nastavPosun(Math.max(0, Math.min(1, dx / sirka)));
        };
        const konec = () => {
            if (!g) return;
            const vodo = g.vodo;
            g = null;
            if (!vodo) return;
            setTaha(false);
            if (posunRef.current > PRAH) otevriRef.current?.();
            else nastavPosun(0);
        };

        el.addEventListener('touchstart', start, { passive: true });
        el.addEventListener('touchmove', move, { passive: false });
        el.addEventListener('touchend', konec);
        el.addEventListener('touchcancel', konec);
        return () => {
            el.removeEventListener('touchstart', start);
            el.removeEventListener('touchmove', move);
            el.removeEventListener('touchend', konec);
            el.removeEventListener('touchcancel', konec);
        };
    }, [plochaRef, otevreno, sirka]);

    // ── Zavírací gesto (za hlavičku panelu) ───────────────────────────
    const zavrGesto = useRef(null);
    const zavriStart = (e) => {
        zavrGesto.current = { x0: e.touches[0].clientX };
        setTaha(true);
    };
    const zavriMove = (e) => {
        if (!zavrGesto.current) return;
        const dx = e.touches[0].clientX - zavrGesto.current.x0;
        nastavPosun(Math.max(0, Math.min(1, 1 - dx / sirka)));
    };
    const zavriKonec = () => {
        if (!zavrGesto.current) return;
        zavrGesto.current = null;
        setTaha(false);
        if (posunRef.current < 1 - PRAH) zavriRef.current?.();
        else nastavPosun(1);
    };

    const t = taha ? posun : (otevreno ? 1 : 0);
    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    return (
        <>
            <style>{`
                @keyframes panel-uchyt-kyv {
                    0%, 100% { transform: translateY(-50%) translateX(0); }
                    50%      { transform: translateY(-50%) translateX(-6px); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .panel-uchyt { animation: none !important; }
                }
            `}</style>

            {/* Úchyt — jediné, co o panelu prozradí, že tam je */}
            <button type="button" className="panel-uchyt"
                onClick={() => otevriRef.current?.()}
                aria-label={`Zobrazit ${titulek.toLowerCase()}`}
                style={{
                    position: 'absolute', right: 0, top: '50%', zIndex: 18,
                    transform: 'translateY(-50%)',
                    width: 21, height: 78, padding: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                    borderRadius: '10px 0 0 10px',
                    border: `1px solid ${barva}55`, borderRight: 'none',
                    background: isDark
                        ? `color-mix(in srgb, ${barva} 22%, rgba(20,20,32,0.72))`
                        : `color-mix(in srgb, ${barva} 14%, rgba(255,255,255,0.86))`,
                    backdropFilter: ROZOSTRENI_NAVIGACE, WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
                    color: barva, cursor: 'pointer',
                    opacity: t > 0 ? 0 : 1,
                    // `visibility` (ne jen opacity) — průhledné tlačítko by jinak
                    // zůstalo v pořadí tabulátoru a čtečka by ho pořád četla.
                    visibility: t > 0 ? 'hidden' : 'visible',
                    transition: `opacity 0.18s ease, visibility 0s linear ${t > 0 ? '0.18s' : '0s'}`,
                    animation: 'panel-uchyt-kyv 1.5s ease-in-out 0.7s 2',
                    WebkitTapHighlightColor: 'transparent',
                }}>
                <ChevronLeft size={15} strokeWidth={2.6} />
                {Ikona && <Ikona size={13} strokeWidth={2.2} />}
            </button>

            {/* Clona — bez blur, ať tah drží plynulost i na slabším telefonu */}
            <div aria-hidden="true" onClick={() => zavriRef.current?.()}
                style={{
                    position: 'absolute', inset: 0, zIndex: 19,
                    background: isDark ? 'rgba(0,0,0,0.38)' : 'rgba(30,30,50,0.16)',
                    opacity: t,
                    pointerEvents: t > 0 ? 'auto' : 'none',
                    transition: taha ? 'none' : 'opacity 0.26s cubic-bezier(0.4, 0, 0.2, 1)',
                }} />

            {/* Panel — v DOM pořád (kvůli plynulému výjezdu), obsah až po prvním otevření */}
            <aside role="dialog" aria-label={titulek} aria-hidden={t === 0}
                style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 20,
                    width: sirka, maxWidth: '100%',
                    display: 'flex', flexDirection: 'column',
                    background: 'var(--surface-raised)',
                    borderLeft: `1px solid ${hranice}`,
                    boxShadow: t > 0 ? '-14px 0 34px rgba(0,0,0,0.28)' : 'none',
                    transform: `translateX(${(1 - t) * 100}%)`,
                    pointerEvents: t > 0 ? 'auto' : 'none',
                    // Zavřený panel se schová až po dojetí — `visibility` ho vyndá
                    // z pořadí tabulátoru, zpožděný přechod nechá dojet animaci.
                    visibility: t > 0 ? 'visible' : 'hidden',
                    transition: taha ? 'none'
                        : `transform 0.26s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${t > 0 ? '0s' : '0.26s'}`,
                    willChange: 'transform',
                }}>
                {/* Hlavička je zároveň úchop pro zavírací tah */}
                <div onTouchStart={zavriStart} onTouchMove={zavriMove} onTouchEnd={zavriKonec} onTouchCancel={zavriKonec}
                    className="flex items-center gap-2"
                    style={{
                        padding: '10px 10px 10px 12px', flexShrink: 0,
                        borderBottom: `1px solid ${hranice}`,
                        touchAction: 'pan-y',
                    }}>
                    <span aria-hidden="true" style={{ width: 4, height: 26, borderRadius: 999, background: `${barva}66`, flexShrink: 0 }} />
                    {Ikona && <Ikona size={14} style={{ color: barva, flexShrink: 0 }} />}
                    <span className="text-[12px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{titulek}</span>
                    <button type="button" onClick={() => zavriRef.current?.()} title="Zavřít" aria-label="Zavřít"
                        className="flex items-center justify-center rounded-lg"
                        style={{ marginLeft: 'auto', width: 30, height: 30, flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                        <X size={16} />
                    </button>
                </div>

                <div className="flex flex-col gap-3"
                    style={{
                        flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                        padding: 12,
                        // Panel jde až ke spodní hraně; místo pro Palec se nechává
                        // uvnitř scrollu, ať konec obsahu jde vytáhnout nad odznak.
                        ...(rezervaDole ? { paddingBottom: `calc(${rezervaDole} + 12px)` } : null),
                    }}>
                    {bylOtevren ? (typeof obsah === 'function' ? obsah() : obsah) : null}
                </div>
            </aside>
        </>
    );
}
